import { useEffect, useState } from 'react'
import { Label } from '../ui/label'
import { Button } from '../ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
import { Package, Check, ChevronsUpDown } from 'lucide-react'
import { Variant } from 'store/types/searchResults'
import { cn } from '../lib/utils'

interface VariantSelectorProps {
  variants: Variant[]
  value?: string // <- variantTag from parent
  onChange: (variant: Variant) => void
}

export const VariantSelector = ({
  variants,
  value,
  onChange,
}: VariantSelectorProps) => {
  const [open, setOpen] = useState(false)

  const handleSelect = (variantTag: string) => {
    const variant = variants.find((v) => v.variantTag === variantTag)
    if (variant) onChange(variant)
    setOpen(false)
  }

  const selectedVariant = variants.find((v) => v.variantTag === value)

  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-2 text-sm font-medium">
        <Package className="h-4 w-4 text-chart-2" />
        Product Variant
      </Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
          >
            {selectedVariant ? selectedVariant.variant : 'Select a variant'}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0" align="start">
          <Command>
            <CommandInput placeholder="Search variant..." />
            <CommandList>
              <CommandEmpty>No variant found.</CommandEmpty>
              <CommandGroup>
                {variants.map((variant) => (
                  <CommandItem
                    key={variant.variantTag}
                    value={variant.variant}
                    onSelect={() => handleSelect(variant.variantTag)}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        variant.variantTag === value
                          ? 'opacity-100'
                          : 'opacity-0'
                      )}
                    />
                    {variant.variant}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}
